import 
	React,
	{
		useState,
		useRef,
		useEffect,
		RefObject,
		MutableRefObject,
} from 'react';
import * as PIXI from "pixi.js";
import type { Dict } from '@pixi/utils';
import useFontFaceObserver from 'use-font-face-observer';

import * as FBRConstants from './FBRConstants';
import * as FBRTypes from './FBRTypes';
import * as ManagedPIXIObjects from './ManagedPIXIObjects';

import style from './FizzBuzzRevolution.module.css';

function createTextChoiceResult(
	textRepresentation: FBRTypes.TextRepresentation,
	startX: number,
	startY: number, 
	yStepAmount: number,
	pixiAppRef: MutableRefObject<PIXI.Application | undefined>,
	centerOffsetContainerRef: MutableRefObject<PIXI.Container | undefined>,
	indicatorsRef: MutableRefObject<{[key:number]:FBRTypes.ChoiceResultIndicator}>,
) {
		if(pixiAppRef.current != null) {
			const newChoiceResultIndicator = ManagedPIXIObjects.createChoiceResultIndicator(
				null,
				textRepresentation, 
				startX, 
				startY, 
				yStepAmount,
				pixiAppRef,
				centerOffsetContainerRef,
				indicatorsRef,
			);
			indicatorsRef.current[newChoiceResultIndicator.id] = newChoiceResultIndicator;
		}
};

function createIconChoiceResult(
	icon: PIXI.Texture,
	startX: number,
	startY: number, 
	yStepAmount: number,
	pixiAppRef: MutableRefObject<PIXI.Application | undefined>,
	centerOffsetContainerRef: MutableRefObject<PIXI.Container | undefined>,
	indicatorsRef: MutableRefObject<{[key:number]:FBRTypes.ChoiceResultIndicator}>,
) {
		if(pixiAppRef.current != null) {
			const newChoiceResultIndicator = ManagedPIXIObjects.createChoiceResultIndicator(
				icon,
				null, 
				startX, 
				startY, 
				yStepAmount,
				pixiAppRef,
				centerOffsetContainerRef,
				indicatorsRef,
			);
			indicatorsRef.current[newChoiceResultIndicator.id] = newChoiceResultIndicator;
		}
};

function createCombinedChoiceResult(
	icon: PIXI.Texture,
	textRepresentation: FBRTypes.TextRepresentation,
	startX: number,
	startY: number, 
	yStepAmount: number,
	pixiAppRef: MutableRefObject<PIXI.Application | undefined>,
	centerOffsetContainerRef: MutableRefObject<PIXI.Container | undefined>,
	indicatorsRef: MutableRefObject<{[key:number]:FBRTypes.ChoiceResultIndicator}>,
) {
		if(pixiAppRef.current != null) {
			const newChoiceResultIndicator = ManagedPIXIObjects.createChoiceResultIndicator(
				icon,
				textRepresentation, 
				startX, 
				startY, 
				yStepAmount,
				pixiAppRef,
				centerOffsetContainerRef,
				indicatorsRef,
			);
			indicatorsRef.current[newChoiceResultIndicator.id] = newChoiceResultIndicator;
		}
};

function incrementScore(
	changeAmount: number,
	globalState: MutableRefObject<FBRTypes.GlobalState>,
) {
	if(globalState.current != null) {
		globalState.current.score = globalState.current.score + changeAmount;
	}
};

function incrementLevel(
	changeAmount: number,
	globalState: MutableRefObject<FBRTypes.GlobalState>,
) {
	if(globalState.current != null) {
		globalState.current.level = globalState.current.level + changeAmount;
	}
};

function FizzBuzzRevolution() {
	let [initializationStarted, setInitializationStarted] = useState(false);
	let [loaded, setLoaded] = useState(false);
	let [texturesLoaded, setTexturesLoaded] = useState(false);
	let fontListLoaded = useFontFaceObserver([
		{ family: 'Odibee Sans' },
		{ family: 'Quantico' },
	]);

	const globalState = useRef<FBRTypes.GlobalState>({...FBRConstants.GLOBAL_STATE_DEFAULT});
	
	const gameContainerRef = useRef<HTMLDivElement>(null);
	const gameCanvasRef = useRef<HTMLCanvasElement>(null);
	const loadScreenRef = useRef<FBRTypes.LoadScreenObject>();
	const pixiAppRef = useRef<PIXI.Application>();
	const centerOffsetContainerRef = useRef<PIXI.Container>(new PIXI.Container());

	const pointerX = useRef<number>(0);
	const pointerY = useRef<number>(0);

	const tilesRef = useRef<FBRTypes.Tiles>({});
	const selectedTileIds = useRef<number[]>([]);

	const indicatorsRef = useRef<{[key:number]:FBRTypes.ChoiceResultIndicator}>({});

	const currentlyFlicking = useRef<boolean>(false);
	const flickStartPoint = useRef<PIXI.Point>(new PIXI.Point(0,0));
	const flickItemOriginalStartPoint = useRef<PIXI.Point>(new PIXI.Point(0,0));

	const textureLoaderRef = useRef<PIXI.Loader>(new PIXI.Loader());
	const textureResourcesRef = useRef<Dict<PIXI.LoaderResource>>();
	const texturesLoadedRef = useRef<boolean>(false);
	const texturesStartedLoadingRef = useRef<boolean>(false);

	//handle initialization	
	useEffect( () => {
		if(initializationStarted) { 
			return;
		}
		setInitializationStarted(true);
		if(
			pixiAppRef.current == null 
			&& gameContainerRef.current != null 
			&& gameCanvasRef.current != null 
		) {
			//console.log('create pixi app');
			pixiAppRef.current = new PIXI.Application({
				width: FBRConstants.STAGE_WIDTH,
				height: FBRConstants.STAGE_HEIGHT,
				view: gameCanvasRef.current
			});
			gameContainerRef.current.appendChild(pixiAppRef.current.view);
			//create loading screen 
			loadScreenRef.current = ManagedPIXIObjects.createLoadScreen();
			pixiAppRef.current.stage.addChild(loadScreenRef.current.container);
			pixiAppRef.current.ticker.add( (delta) => {
				//update loading indicator
				if(loadScreenRef.current != null) {
					loadScreenRef.current.update();
				}
			});
		}
	}, [initializationStarted]);

	//handle loading textures
	useEffect( () => {
		//ensure fonts are loaded before loading textures
		if(!fontListLoaded) {
			//console.warn('fonts not loaded');
			return;
		}
		//load textures
		if(!texturesStartedLoadingRef.current) {
			texturesStartedLoadingRef.current = true;
			let totalResources = Object.keys(FBRConstants.namedGameResources).length;
			let loadedResourcesCount = 0;
			textureLoaderRef.current.onProgress.add(() => {
				loadedResourcesCount++;
				//console.log(`loadedResourcesCount = ${loadedResourcesCount} / totalResources = ${totalResources} = ${Math.round((loadedResourcesCount/totalResources)*100)}`); 
				if(loadScreenRef.current != null) {
					loadScreenRef.current.setLoadedValue(Math.round((loadedResourcesCount/totalResources)*100));
				}
			});
			Object.keys(FBRConstants.namedGameResources).forEach( (resourceKey) => {
				textureLoaderRef.current.add( resourceKey, FBRConstants.namedGameResources[resourceKey]);
			});
			textureLoaderRef.current.load((loader, resources) => {
				textureResourcesRef.current = resources;
				setTexturesLoaded(true);
				if(loadScreenRef.current != null) {
					loadScreenRef.current.destroy();
				}
				globalState.current.gameState = 'splash';
				//console.log('textures loaded');
			});
		}
	},
	[fontListLoaded]);

	//handle game setup
	useEffect( () => {
		//ensure textures are loaded before continuing setup
		if(!texturesLoaded) {
			//console.warn('textures not loaded');
			return;
		}
		//initialize allTimeMaxScore and allTimeMaxLevel from localStorage
		globalState.current.allTimeMaxScore = parseInt(localStorage.getItem('allTimeMaxScore') ?? globalState.current.allTimeMaxScore.toString());
		globalState.current.allTimeMaxLevel = parseInt(localStorage.getItem('allTimeMaxLevel') ?? globalState.current.allTimeMaxLevel.toString());
		//create pixi app
		if(
			pixiAppRef.current != null 
			&& gameContainerRef.current != null 
			&& gameCanvasRef.current != null
		) {
			//make stage interactive
			pixiAppRef.current.stage.interactive = true;
			//setup video status container
			const videoStatusContainerObject = ManagedPIXIObjects.createVideoStatusContainer(
				[textureResourcesRef.current!.statusStart1.data!],
				[
					textureResourcesRef.current!.statusSuccess1.data!,
					textureResourcesRef.current!.statusSuccess2.data!,
					textureResourcesRef.current!.statusSuccess3.data!,
					textureResourcesRef.current!.statusSuccess4.data!,
					textureResourcesRef.current!.statusSuccess5.data!,
					textureResourcesRef.current!.statusSuccess7.data!,
					textureResourcesRef.current!.statusSuccess8.data!,
					textureResourcesRef.current!.statusSuccess9.data!,
					textureResourcesRef.current!.statusSuccess10.data!,
					textureResourcesRef.current!.statusSuccess11.data!,
				],
				[
					textureResourcesRef.current!.statusFail1.data!,
					textureResourcesRef.current!.statusFail3.data!,
					textureResourcesRef.current!.statusFail4.data!,
					textureResourcesRef.current!.statusFail5.data!,
					textureResourcesRef.current!.statusFail6.data!,
					textureResourcesRef.current!.statusFail7.data!,
					textureResourcesRef.current!.statusFail8.data!,
					textureResourcesRef.current!.statusFail9.data!,
					textureResourcesRef.current!.statusFail10.data!,
					textureResourcesRef.current!.statusFail11.data!,
				],
				pixiAppRef,
				globalState,
			);
			pixiAppRef.current.stage.addChild(videoStatusContainerObject.container);
			//setup noice overlay
			const noiceOverlayObject = ManagedPIXIObjects.createNoiceOverlay(
				[
					textureResourcesRef.current!.noiceVideo1.data!,
					textureResourcesRef.current!.noiceVideo2.data!,
					textureResourcesRef.current!.noiceVideo3.data!,
					textureResourcesRef.current!.noiceVideo4.data!,
					textureResourcesRef.current!.noiceVideo5.data!,
				]
			);
			pixiAppRef.current.stage.addChild(noiceOverlayObject.container);
			//add game background
			const gameBackgroundSprite = PIXI.Sprite.from(textureResourcesRef.current!.backgroundOverlay.texture!);
			pixiAppRef.current.stage.addChild(gameBackgroundSprite);
			//setup streak overlay
			const streakStatusContainerObject = ManagedPIXIObjects.createStreakStatusContainer(
				pixiAppRef,
				globalState,
				incrementScore,
			);
			pixiAppRef.current.stage.addChild(streakStatusContainerObject.container);
			//setup info container
			const infoBarObject = ManagedPIXIObjects.createInfoBar(
				pixiAppRef,
				globalState,
			);	
			pixiAppRef.current.stage.addChild(infoBarObject.container);	
			//update centerOffsetContainer properties and add to the stage
			centerOffsetContainerRef.current.x = pixiAppRef.current.screen.width / 2;
			centerOffsetContainerRef.current.y = pixiAppRef.current.screen.height / 2;
			pixiAppRef.current.stage.addChild(centerOffsetContainerRef.current);	
			//add flick direction indicator
			const flickDirectionIndicatorObject = ManagedPIXIObjects.createFlickDirectionIndicator(
				pointerX,
				pointerY,
				currentlyFlicking,
				flickItemOriginalStartPoint,
			);
			pixiAppRef.current.stage.addChild(flickDirectionIndicatorObject.container);
			//create instruction screen
			const instructionScreenObject = ManagedPIXIObjects.createInstructionScreen(
				textureResourcesRef.current!.instructionScreenBackground.texture!,
				textureResourcesRef.current!.instructionsExitButton.texture!,
			);
			//add splash screen
			const splashScreenObject = ManagedPIXIObjects.createSplashScreen(
				textureResourcesRef.current!.splashScreenBackground.texture!,
				textureResourcesRef.current!.startButton.texture!,
				textureResourcesRef.current!.instructionsButton.texture!,
				instructionScreenObject.setVisibility,
				pixiAppRef,
				globalState,
			);
			pixiAppRef.current.stage.addChild(splashScreenObject.container);
			//add instruction screen 
			pixiAppRef.current.stage.addChild(instructionScreenObject.container);
			//add lose screen
			const loseScreenObject = ManagedPIXIObjects.createLoseScreen(
				textureResourcesRef.current!.loseScreenBackground.texture!,
				textureResourcesRef.current!.playAgainButton.texture!,
				pixiAppRef,
				globalState,
			);
			pixiAppRef.current.stage.addChild(loseScreenObject.container);
			//main update handler - handle updating state and managed objects
			pixiAppRef.current.ticker.add((delta) => {
				//update tile objects
				for(const tileId in tilesRef.current) {
					tilesRef.current[tileId].update(delta);
				}
				//if lost, destroy all existing tiles
				if(globalState.current.gameState == 'lost_game') {
					for(const tileId in tilesRef.current) {
						tilesRef.current[tileId].setState('destroy');
					}
				}
				//update indicatorsRef
				for(const indicatorId in indicatorsRef.current) {
					indicatorsRef.current[indicatorId].update(delta);
				};
				//handle incrementing current level counter
				if(
					globalState.current != null 
					&& globalState.current.gameState == 'running'
					&& Date.now() - globalState.current.gameStartTime > (globalState.current.level * FBRConstants.LEVEL_DURATION_MS)
				) {
					//console.log('change level +1');
					incrementLevel(1, globalState);
				}
				//handle maxTileValue update in response to level
				if(globalState.current.level >= 30) {
					globalState.current.maxTileValue = 450;
				} else if (globalState.current.level >= 25) {
					globalState.current.maxTileValue = 300;
				} else if (globalState.current.level >= 20) {
					globalState.current.maxTileValue = 200;
				} else if (globalState.current.level >= 10) {
					globalState.current.maxTileValue = 150;
				} else if (globalState.current.level >= 5) {
					globalState.current.maxTileValue = 100;
				}
				//track max score
				if(globalState.current.score > globalState.current.maxScore) {
					globalState.current.maxScore = globalState.current.score;
				}
				//track max level
				if(globalState.current.level > globalState.current.maxLevel) {
					globalState.current.maxLevel = globalState.current.level;
				}
				//track all time max score
				if(globalState.current.score > globalState.current.allTimeMaxScore) {
					globalState.current.allTimeMaxScore = globalState.current.score;
					localStorage.setItem('allTimeMaxScore', globalState.current.maxScore.toString());
				}
				//track all time max level
				if(globalState.current.level > globalState.current.allTimeMaxLevel) {
					globalState.current.allTimeMaxLevel = globalState.current.level;
					localStorage.setItem('allTimeMaxLevel', globalState.current.maxLevel.toString());
				}
				//handle losing game
				if(globalState.current.score < FBRConstants.MIN_SCORE_FOR_LOSS) {
					//console.log('lost current game');
					globalState.current.gameState = 'lost_game';
				}
				//update managed objects
				flickDirectionIndicatorObject.update();
				splashScreenObject.update();
				infoBarObject.update();
				loseScreenObject.update();
				videoStatusContainerObject.update();
				noiceOverlayObject.update();
				streakStatusContainerObject.update();
			});
			//tile generation handler - generate tiles constantly
			pixiAppRef.current.ticker.add((
				() => {
					let prevTime = Date.now();
					return (delta) => {
						const levelDivisor = globalState.current.level/2 < 1 ? 1 : globalState.current.level/2;
						const tileGenerationThreshold = (globalState.current.maxTileGenerationDelay/levelDivisor)
						const tileGenerationThresholdClamped = tileGenerationThreshold > 32 ? tileGenerationThreshold : 32;
						if(
							globalState.current.gameState == 'running'
							&& Date.now() - prevTime > tileGenerationThresholdClamped - delta
						) {
							//console.log('add new tile');
							//if(pixiAppRef.current != null) {
								const newTileSpeed = (globalState.current.level/2 < 10 ? globalState.current.level/2 : 10) + 1;
								let newTile = ManagedPIXIObjects.createTile(
									textureResourcesRef.current!.tileGraphic.texture!,
									textureResourcesRef.current!.happyFace.texture!,
									textureResourcesRef.current!.sadFace.texture!,
									0, 
									newTileSpeed,
									globalState.current.maxTileValue,
									centerOffsetContainerRef,
									createTextChoiceResult,
									createIconChoiceResult,
									createCombinedChoiceResult,
									incrementScore,
									tilesRef,
									globalState,
									noiceOverlayObject.showNoiceVideo,
									pixiAppRef,
									indicatorsRef,
								);
								tilesRef.current[newTile.id] = newTile;
								prevTime = Date.now();
							//}
						}
					}
				}
			)());
			//track pointer position within stage
			pixiAppRef.current.stage.on('pointermove', (event:PIXI.InteractionEvent) => {
				//console.log('pointer moved');
				//console.dir(event);
				pointerX.current = event.data.global.x;
				pointerY.current = event.data.global.y;
			});
			//handle pointerdown events with respect to PIXI app
			pixiAppRef.current.stage.on('pointerdown', (event:PIXI.InteractionEvent) => {
				if(pixiAppRef.current != null) {
					pointerX.current = event.data.global.x;
					pointerY.current = event.data.global.y;
					flickItemOriginalStartPoint.current = new PIXI.Point(event.data.global.x, event.data.global.y);
					//console.log('flick start!');
					//console.dir(flickStartPoint.current);
					let obj = pixiAppRef
						.current
						.renderer
						.plugins
						.interaction
						.hitTest(event.data.global);
					//console.dir(obj);
					if(obj != null && obj.name != null) {
						currentlyFlicking.current = true;
						selectedTileIds.current.push(obj.name);
						tilesRef.current[obj.name].setState('selected');
					}
				}
			});
			//handle pointerdown events with respect to document
			document.addEventListener('pointerdown', (event:PointerEvent) => {
				//console.log('global pointerdown fired');
				//console.dir(event);
				flickStartPoint.current = new PIXI.Point(event.clientX, event.clientY);
			});
			//handle pointerup events with respect to document
			document.addEventListener('pointerup', (event:PointerEvent) => {
				//console.log('global pointerup fired');
				//do flick calculations
				//console.log('flick end!');
				currentlyFlicking.current = false;
				let flickXSpeed;
				let flickYSpeed;
				if(flickStartPoint.current != null && pixiAppRef.current != null) {
					const flickXDelta = event.clientX - flickStartPoint.current.x;
					const flickYDelta = event.clientY - flickStartPoint.current.y;
					//console.log(`delta: x=${flickXDelta}, y=${flickYDelta}`);
					//find ratios with respect to screen width
					const flickXDeltaRatio = flickXDelta/pixiAppRef.current.screen.width;
					const flickYDeltaRatio = flickYDelta/pixiAppRef.current.screen.width;
					//console.log(`deltaRatio: x=${flickXDeltaRatio}, y=${flickYDeltaRatio}`);
					flickXSpeed = flickXDeltaRatio*FBRConstants.MAX_FLICK_SPEED;	
					flickYSpeed = flickYDeltaRatio*FBRConstants.MAX_FLICK_SPEED;	
					//console.log(`flickSpeed: x=${flickXSpeed}, y=${flickYSpeed}`);
				}
				//handle setting after flick state
				while( selectedTileIds.current.length > 0) {
					const currentId = selectedTileIds.current.pop();
					if(currentId != null && tilesRef.current[currentId] != null) {
						tilesRef.current[currentId].setSpeed(flickXSpeed, flickYSpeed);
						tilesRef.current[currentId].setState('flicked');
					}
				}
			});
		}
		//app is loaded 
		setLoaded(true);
	},
	[texturesLoaded]);
	
  return (
    <div className="App">
			<div ref={gameContainerRef} className={style.gameContainer}>
				<canvas ref={gameCanvasRef} className={style.gameCanvas}/>
			</div>
    </div>
  );
}

export default FizzBuzzRevolution;
